import { JSX, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';

import { useGlobal } from '~/hooks/useGlobal';

import Breadcrumbs from '../../../components/Breadcrumbs/Breadcrumbs';
import { BreadcrumbsItem } from '../../../stores/CatalogStore';

import MobileHeaderLayout from '../../Main/MobileHeader/MobileHeaderLayout';
import MobileHeaderTitle from '../../Main/MobileHeader/MobileHeaderTitle';

import styles from '../Legals.module.scss';

import mdStyles from './LegalsHeimkaup.module.scss';

import '../styles.scss';

type Props = {
  handleLink: (anchor: string) => void;
};

const LEGALS_NAV = [
  {
    link: 'terms-and-conditions',
    title: 'Terms and Conditions',
  },
  {
    link: 'privacy-policy-main',
    title: 'Privacy Policy',
  },
];

const text = `
<h2 id="terms-and-conditions">Terms & Conditions</h2>

1. ### General

    These Terms & Conditions (“Terms”) govern the use of the website and the services provided by Icelandic Trading (kennitala: 4603242380), located at Hverfisgata 18, 101 Reykjavík, Iceland (“we,” “us,” or “our”). By accessing or using our website, you agree to be bound by these Terms and any relevant laws or regulations. If you do not agree with these Terms, you must not use our website or services.

2. ### Definitions

    - “Website” refers to any webpage operated by Icelandic Trading.
    - “You” or “User” refers to the individual or entity accessing or using our Website.
    - “We,” “our,” or “us” refers to Icelandic Trading.
    - “Services” refers to any product or service we provide, including the sale and delivery of groceries or other goods.

3. ### Scope of These Terms

    These Terms apply to all use of our Website, Services, and any internal pages or subdomains of our Website. Additional or separate terms may apply to specific features, tools, or offers. In the event of any conflict between these Terms and separate terms, those separate terms will control only for the specific feature, tool, or offer.

4. ### Data Protection and Cookies

    We are committed to respecting your privacy. By using our Website, you agree to our Privacy Policy, which is incorporated by reference into these Terms. Our Website may use cookies to analyze usage and to improve user experience. Cookies are small files stored on your device that help us track and remember certain information. You can choose whether to accept or decline cookies through your browser settings. However, if you decline cookies, certain parts of our Website may not function properly.

    For more information on how we collect, use, and protect your data, please review our Privacy Policy.

5. ### Intellectual Property

    All content on our Website—such as text, graphics, logos, images, data compilations, and software—is protected by intellectual property laws. You may not reproduce, distribute, modify, sell, or otherwise exploit any of this material without our express written permission. You may view or print pages for personal, non-commercial use only.

6. ### Security

    You are responsible for maintaining the confidentiality of your account information, including passwords, and for restricting access to your devices. By creating an account, you agree to accept responsibility for any and all activities that occur under your account. If you suspect unauthorized use of your account or any other security breach, please notify us immediately so we can take appropriate action.

7. ### Changes to These Terms

    We reserve the right to revise these Terms at any time. Any changes become effective immediately once posted on our Website. By continuing to use our Website or Services after the revised Terms are posted, you agree to be bound by them. We encourage you to review these Terms periodically.

8. ### Links to Third-Party Websites

    Our Website may contain links or references to other websites. These links are provided for convenience only. We do not endorse and are not responsible for the content, security, or privacy practices of any third-party websites. Your use of such websites is at your own risk and subject to their respective policies.

9. ### Disclaimer of Liability

    Our Website and all materials and Services are provided “as is” and without any warranties of any kind, express or implied. We do not warrant that:

    - The Website will be uninterrupted, error-free, or secure.
    - The information or materials found on the Website are accurate, complete, or current.
    - Any particular outcome or result will arise from the use of our Website or Services.

    We shall not be liable for any direct, indirect, incidental, consequential, or special damages arising out of or in any way connected with your use of our Website or Services, to the extent permitted by Icelandic law.

10. ### Indemnification

    You agree to indemnify and hold harmless Icelandic Trading, its officers, directors, employees, and agents from and against any claims, losses, liabilities, damages, costs, or expenses (including legal fees) arising out of or related to your use or misuse of our Website or Services, violation of these Terms, or infringement of any third-party rights.

11. ### Miscellaneous

    - No Waiver: Failure to assert any right or provision under these Terms shall not constitute a waiver of such right or provision.
    - Severability: If any part of these Terms is deemed invalid or unenforceable, the remaining provisions shall remain in effect.
    - Entire Agreement: These Terms, together with any policies or legal notices on our Website, constitute the entire agreement between you and Icelandic Trading regarding the use of our Website and Services.

12. ### Governing Law and Jurisdiction

    These Terms and any disputes or claims arising out of or in connection with them or their subject matter (including non-contractual disputes or claims) shall be governed by and construed in accordance with the laws of Iceland. You agree to submit to the exclusive jurisdiction of the Icelandic courts to resolve any dispute or claim related to these Terms or your use of the Website and Services.

13. ### Contact Us

    If you have any questions about these Terms & Conditions, please contact:
    Icelandic Trading  
    Hverfisgata 18  
    101 Reykjavík, Iceland  
    Kenitala: 4603242380  
    Email: info@icelandictrading.is   
    Last Updated: 10.03.25

<h2 id="privacy-policy-main">Privacy Policy</h2>

1. ### Introduction
    Welcome to Icelandic Trading (kennitala: 4603242380), located at Hverfisgata 18, 101 Reykjavík, Iceland (“we,” “us” or “our”). We respect your privacy and are committed to protecting your personal information. This Privacy Policy explains how and why we collect, use, and safeguard personal data about our customers and website visitors. It also describes your rights regarding the personal data we hold about you.

    By accessing or using our services and website, you consent to this Privacy Policy. If you disagree with any portion of it, you should discontinue use of our services and website.

2. ### When Do We Collect Your Information and Why?

    We collect information at various points, for example:
    - When you make a purchase on our online store or create an account with us.
    - When you request information (e.g., newsletters or marketing messages).
    - When you volunteer additional information for personalized services.
    - When you use our website (through cookies, IP addresses, device information, etc.).
    - When you contact us via email, phone, or other communication methods (e.g., social media channels).

    We collect personal data to:
    - Provide the products or services you request.
    - Keep track of purchase history for a seamless, personalized experience.
    - Stay in touch with you and send updates, offers, and newsletters (if you opt in).
    - Maintain, develop, and improve our online platform and services.

3. ### What Personal Data Do We Collect?

    The type of personal data we collect depends on the services you use and how you interact with us. Such information may include:
    - Basic identification details: Name, kennitala, address, phone number, email.
    - Financial details: Credit/debit card information, bank account information, or payment details.
    - Purchase history: Items you have bought, dates of transactions, relevant transactional details.
    - Communications: Emails, messages, call recordings (where applicable), or other correspondence.
    - Website usage information: Such as your IP address, browser type, device type, and information collected through cookies when you visit our site.
    - Optional information: Additional details you may voluntarily provide to us for personalization or enhanced services (e.g., preferences or demographic information). If you represent a corporate customer, supplier, or other business partner, we may collect your contact information (e.g., name, email, phone number) and communication history to fulfill contractual obligations.

4. ### How Do We Use Your Personal Data?

    We may use your personal data to:
    - Fulfill orders and provide services you have requested.
    - Process payments and send invoices or payment reminders.
    - Manage and analyze your purchase history for customer support, returns, or warranty purposes.
    - Communicate promotions and offers that may interest you, if you have opted in.
    - Improve our products and services by analyzing how you interact with our website.
    - Respond to inquiries or complaints you submit.
    - Ensure security and protect our property, staff, customers, and visitors.

5. ### With Whom Do We Share Your Personal Data?
    
    We only share your personal data as reasonably necessary and in accordance with applicable laws:
    - Service providers: Such as payment processors, IT hosting companies, delivery/logistics partners, or marketing services.
    - Legal authorities: Courts, law enforcement, or regulators if required by law or to protect our rights.
    - Professional advisors: Lawyers, accountants, auditors, or other consultants who assist us in carrying out our business activities.
    - Business transactions: In case of a merger, acquisition, or sale of our business or assets, your personal data may be transferred to a successor entity.

    Any third party with whom we share your information is bound by contractual agreements to protect and use your data only as necessary to perform their obligations.

6. ### How Do We Ensure the Security of Your Personal Data?

    We store personal data in secure, access-controlled systems. We have put in place technical and organizational measures to safeguard your data:
    - Restricted access to personal data only to staff who need it to perform their tasks.
    - Regular software updates and security scans to protect our systems.
    - Use of recognized service providers for data storage and security.

    Despite our efforts, no security system is impenetrable. By using our services, you acknowledge and accept that data breaches can occur and that we bear no liability for unauthorized access beyond our reasonable control.

7. ### How Long Do We Retain Your Personal Data?
    
    We retain your personal information for as long as necessary to:
    - Provide the services you have requested.
    - Comply with legal obligations (e.g., retention requirements under accounting or tax laws).
    - Resolve disputes or enforce agreements.
    - Accommodate legitimate business needs.

    Once there is no longer a legal or business need to store your personal data, we will securely delete or anonymize it.

8. ### Your Rights Regarding Your Personal Data

    Under applicable data protection laws, you have the right to:
    - Access: Request a copy of the personal data we hold about you.
    - Rectification: Correct or update inaccurate or incomplete data.
    - Erasure: Request deletion of your data where there is no legitimate reason to continue processing it.
    - Restriction: Request limiting how we process your data in certain circumstances.
    - Objection: Object to processing activities, such as direct marketing.
    - Data Portability: Obtain a copy of your data in a commonly used format to transfer it to another service.

    To exercise these rights, please contact us at the details in Section 10 below. We will do our best to respond promptly and always within the timeframe required by applicable law. You may also lodge a complaint with the Icelandic Data Protection Authority (Persónuvernd) if you believe your rights are being violated.

9. ### Changes to This Privacy Policy

    We may occasionally update this Privacy Policy to reflect changes in our legal or regulatory obligations or in the way we process your personal data. Any updates will be posted on our website with the date of the latest revision. We encourage you to review this page periodically to stay informed about how we protect your personal data.

10. ### Contact Us
    
    For questions about this Privacy Policy or to exercise your rights, please contact:  
    Icelandic Trading  
    Hverfisgata 18  
    101 Reykjavík, Iceland  
    Kenitala: 4603242380
`;

const LegalsHeimkaup = ({ handleLink }: Props): JSX.Element => {
  const { t } = useTranslation();
  const { isTablet } = useGlobal();

  useEffect(() => {
    setTimeout(() => {
      const anchor = window.location.hash.slice(1);
      handleLink(anchor);
    }, 0);
  }, []);

  return (
    <>
      <Helmet>
        <meta data-react-helmet="true" name="robots" content="index, follow" />
      </Helmet>
      <MobileHeaderLayout content={<MobileHeaderTitle text={t('legals')} />} />
      <div className="content-layout legals">
        <div className={styles.root}>
          {!isTablet && (
            <>
              <Breadcrumbs
                items={
                  [
                    {
                      text: t('home'),
                      link: '/',
                    },
                    {
                      text: t('legals'),
                      link: '/legals',
                    },
                  ].filter(Boolean) as BreadcrumbsItem[]
                }
              />

              <h2 className={styles.title}>{t('legals')}</h2>
            </>
          )}

          <div className={styles.page}>
            <div className={styles.page__links_container}>
              <ul className={styles.page__links}>
                {LEGALS_NAV.map((i) => (
                  <li className={styles.page__links_item} key={i.link}>
                    <a
                      href={`#${i.link}`}
                      onClick={(e) => {
                        e.preventDefault();
                        handleLink(i.link);
                      }}
                    >
                      {i.title}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div className={styles.page__content}>
              <ReactMarkdown
                className={mdStyles.mdStyles}
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
              >
                {text}
              </ReactMarkdown>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LegalsHeimkaup;
